<template>
  <div class="page-main page-border">
    <div class="page-header">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">Admin</li>
        <li class="breadcrumb-item">
          <router-link v-bind:to="`/${$route.params.namespace}/admin/teams`">Manage Team</router-link>
        </li>
        <li class="breadcrumb-item active">{{ (team) ? team.name : '' }}</li>
      </ol>
      <h1 class="page-title">Manage Billing</h1>
    </div>
    <div class="page-content">
      <div class="card" v-show="isLoading || !team">
        <div class="card-block">
          <p class="card-text">
            <div class="row">
              <div class="col-md-12 card-stat">
                <loading-indicator></loading-indicator>
              </div>
            </div>
          </p>
        </div>
      </div>
      <div class="card" v-show="!isLoading">
        <Team-info
          v-if="showCurrentTeamInfo"
          v-bind:team="team"
          v-bind:currentUser="currentUser"
          v-on:loadChanged="teamInfoLoadChanged">
        </Team-info>
        <Current-stat
          v-if="showCurrentStat"
          v-bind:team="team"
          v-on:loadChanged="currentStatLoadChanged">
        </Current-stat>
        <Current-package
          v-if="showCurrentPackage"
          v-bind:team="team"
          v-on:loadChanged="currentPackageLoadChanged">
        </Current-package>
        <Manual-credit
          v-if="showManualCredit"
          v-bind:team="team"
          v-on:loadChanged="manualCreditLoadChanged">
        </Manual-credit>
        <div class="card-block" v-if="showBillingInformation">
          <Billing-information
            v-bind:team="team"
            v-on:loadChanged="billingInformationLoadChanged">
          </Billing-information>
        </div>
        <Monthly-usage-history
          v-if="showMonthlyUsageHistory"
          v-bind:team="team"
          v-on:loadChanged="monthlyUsageHistoryLoadChanged">
        </Monthly-usage-history>
        <Statement
          v-if="showStatement"
          v-bind:team="team"
          v-on:loadChanged="statementLoadChanged">
        </Statement>
        <Client-usage-history
          v-bind:team="team"
          v-bind:showTitle="true"
          v-on:loadChanged="clientUsageHistoryLoadChanged"
          v-if="showClientUsageHistory">
        </Client-usage-history>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import TeamInfo from 'components/billings/TeamInfo';
import CurrentStat from 'components/billings/CurrentStat';
import CurrentPackage from 'components/billings/packages/CurrentPackage';
import ManualCredit from 'components/admin/teams/ManualCredit';
import BillingInformation from 'components/billings/payments/BillingInformation';
import Statement from 'components/billings/Statement';
import MonthlyUsageHistory from 'components/billings/usages/MonthlyUsageHistory';
import ClientUsageHistory from 'components/billings/usages/ClientUsageHistory';
import LoadingIndicator from 'components/LoadingIndicator';

export default {
  components: {
    TeamInfo,
    CurrentStat,
    CurrentPackage,
    ManualCredit,
    BillingInformation,
    Statement,
    MonthlyUsageHistory,
    ClientUsageHistory,
    LoadingIndicator,
  },
  computed: {
    ...mapGetters([
      'currentUser',
    ]),
    isLoading: function() {
      return (
        this.isTeamInfoLoading ||
        this.isCurrentStatLoading ||
        this.isManualCreditLoading ||
        this.isBillingInformationLoading ||
        this.isMonthlyUsageHistoryLoading ||
        this.isClientUsageHistoryLoading);
    },
  },
  watch: {
    $route: function() {
      this.fetchTeam();
    },
  },
  data: function () {
    return {
      showCurrentTeamInfo: true,
      showCurrentStat: false,
      showCurrentPackage: false,
      showManualCredit: false,
      showBillingInformation: false,
      showMonthlyUsageHistory: false,
      showStatement: false,
      showClientUsageHistory: false,
      team: null,
      isTeamInfoLoading: false,
      isCurrentStatLoading: false,
      iscurrentPackageLoading: false,
      isManualCreditLoading: false,
      isBillingInformationLoading: false,
      isMonthlyUsageHistoryLoading: false,
      isStatementLoading: false,
      isClientUsageHistoryLoading: false,
    };
  },
  created: function() {
    this.isLoading = true;
  },
  mounted: function() {
    this.fetchTeam();
  },
  methods: {
    fetchTeam: function() {
      const subnamespace = this.$route.params.subnamespace;
      axios.get(`/api/teams/${subnamespace}`)
      .then((response) => {
        this.team = response.data;

        this.checkVisibleComponents();
      })
      .catch(() => {
        // error
      });
    },
    resetVisibleComponents: function() {
      this.showCurrentTeamInfo = true;
      this.showCurrentStat = false;
      this.showCurrentPackage = false;
      this.showManualCredit = false;
      this.showBillingInformation = false;
      this.showMonthlyUsageHistory = false;
      this.showStatement = false;
      this.showClientUsageHistory = false;
    },
    checkVisibleComponents: function() {
      if (this.currentUser.team.is_superadmin) {
        if (!this.team.is_reseller && !this.team.has_reseller) {
          this.resetVisibleComponents();

          // team is normal user
          const isPrePaidTeam = (this.team.pay_plan === 'prepaid');
          this.showCurrentStat = true;
          this.showCurrentPackage = true;
          this.showManualCredit = isPrePaidTeam;
          this.showBillingInformation = true;
          this.showMonthlyUsageHistory = true;
          this.showStatement = true;
        } else if (this.team.is_reseller) {
          this.resetVisibleComponents();

          // team is reseller
          this.showCurrentStat = true;
          this.showCurrentPackage = true;
          this.showBillingInformation = true;
          this.showClientUsageHistory = true;
        } else if (this.team.has_reseller) {
          this.resetVisibleComponents();

          // team is reseller client
          this.showCurrentTeamInfo = true;
          this.showCurrentStat = false;
          this.showCurrentPackage = true;
          this.showMonthlyUsageHistory = true;
        }
      } else if (this.currentUser.team.is_reseller) {
        this.resetVisibleComponents();

        // sub team
        this.showCurrentStat = true;
        this.showMonthlyUsageHistory = true;
      }
    },
    teamInfoLoadChanged: function(value) {
      this.isTeamInfoLoading = value;
    },
    currentStatLoadChanged: function(value) {
      this.isCurrentStatLoading = value;
    },
    currentPackageLoadChanged: function(value) {
      this.iscurrentPackageLoading = value;
    },
    manualCreditLoadChanged: function(value) {
      this.isManualCreditLoading = value;
    },
    billingInformationLoadChanged: function(value) {
      this.isBillingInformationLoading = value;
    },
    monthlyUsageHistoryLoadChanged: function(value) {
      this.isMonthlyUsageHistoryLoading = value;
    },
    statementLoadChanged: function(value) {
      this.isStatementLoading = value;
    },
    clientUsageHistoryLoadChanged: function(value) {
      this.isClientUsageHistoryLoading = value;
    },
  },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// TeamBillingSummary.vue?808eb75a